// src/ui/menu.ts
// Title / setup screen and in-game pause menu, all canvas-rendered.

import { GAME_W, GAME_H } from '../render/canvas';
import { PLAYER_COLORS } from '../sim/state';
import { type AILevel, aiLabel } from '../sim/ai';
import { type WeaponDef } from '../sim/weapons';

export type Screen = 'title' | 'playing' | 'paused' | 'match-over' | 'share-link' | 'code-wait' | 'lobby' | 'shop';

/** Starting-cash tiers. Bigger cash = bigger booms. */
export const CASH_TIERS = [5_000, 10_000, 25_000, 50_000, 100_000, 250_000] as const;
export const DEFAULT_CASH_TIER_INDEX = 1; // $10,000

export interface MenuState {
  screen: Screen;
  playerCount: number;        // 2..4
  names: string[];            // length 4; only first N are used
  aiLevels: AILevel[];        // length 4; per-slot AI skill ('human' = no AI)
  matchTarget: number;        // rounds needed to win the match (1..5)
  wins: number[];             // round wins per player (length = playerCount)
  matchWinner: number;        // -1 = none, else tank index
  startingCash: number;       // host-chosen starting cash
}

export function newMenuState(): MenuState {
  return {
    screen: 'title',
    playerCount: 4,
    names: ['Player 1', 'Player 2', 'Player 3', 'Player 4'],
    aiLevels: ['human', 'human', 'human', 'human'],
    matchTarget: 3,
    wins: [0, 0, 0, 0],
    matchWinner: -1,
    startingCash: CASH_TIERS[DEFAULT_CASH_TIER_INDEX],
  };
}

interface HitBox { x: number; y: number; w: number; h: number; action: string; }
let hitBoxes: HitBox[] = [];

/** Compact cash formatter: $10,000 -> "$10K", $100,000 -> "$100K", $250,000 -> "$250K". */
export function formatCash(n: number): string {
  if (n >= 1000) return `$${Math.round(n / 1000)}K`;
  return `$${n}`;
}

function box(x: number, y: number, w: number, h: number, action: string): HitBox {
  return { x, y, w, h, action };
}

/** Hit-test menu buttons. Returns action string or null. */
export function hitTestMenu(gx: number, gy: number): string | null {
  for (const b of hitBoxes) {
    if (gx >= b.x && gx <= b.x + b.w && gy >= b.y && gy <= b.y + b.h) return b.action;
  }
  return null;
}

// ---------------------- TITLE / SETUP ----------------------

export function drawTitle(ctx: CanvasRenderingContext2D, m: MenuState): void {
  hitBoxes = [];

  // Background gradient
  const bg = ctx.createLinearGradient(0, 0, 0, GAME_H);
  bg.addColorStop(0, '#0a0618');
  bg.addColorStop(1, '#2a0808');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, GAME_W, GAME_H);

  // Glowing title "SCORCH!"
  ctx.save();
  ctx.shadowColor = '#ff5020';
  ctx.shadowBlur = 32;
  ctx.fillStyle = '#ff8030';
  ctx.font = 'bold 96px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('SCORCH!', GAME_W / 2, 110);
  ctx.shadowBlur = 0;
  ctx.fillStyle = '#ffb060';
  ctx.font = '18px monospace';
  ctx.fillText('Mobile Artillery Combat \u2014 Hot-Seat', GAME_W / 2, 140);
  ctx.restore();

  // Player count selector
  ctx.fillStyle = '#ccc';
  ctx.font = 'bold 22px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('PLAYERS', GAME_W / 2, 190);

  const countY = 210;
  const countCenterX = GAME_W / 2;
  drawPillButton(ctx, countCenterX - 140, countY, 60, 44, '\u25C4', m.playerCount > 2);
  hitBoxes.push(box(countCenterX - 140, countY, 60, 44, 'count-dec'));
  drawPillButton(ctx, countCenterX + 80, countY, 60, 44, '\u25BA', m.playerCount < 4);
  hitBoxes.push(box(countCenterX + 80, countY, 60, 44, 'count-inc'));

  ctx.fillStyle = '#fff';
  ctx.font = 'bold 36px monospace';
  ctx.textAlign = 'center';
  ctx.fillText(String(m.playerCount), countCenterX, countY + 34);

  // Name rows (color | name | AI level)
  const rowY0 = 280;
  const rowH = 38;
  const rowW = 540;
  const rowX = (GAME_W - rowW) / 2;
  const aiBtnW = 130;
  const nameBtnX = rowX + 40;
  const nameBtnW = rowW - 40 - 10 - aiBtnW;
  const aiBtnX = rowX + rowW - aiBtnW;
  for (let i = 0; i < m.playerCount; i++) {
    const y = rowY0 + i * rowH;
    const color = PLAYER_COLORS[i % PLAYER_COLORS.length];

    // Color chip
    ctx.fillStyle = color.hex;
    ctx.fillRect(rowX, y, 30, 30);

    // Name button
    ctx.fillStyle = '#222';
    ctx.strokeStyle = '#666';
    ctx.lineWidth = 1;
    ctx.fillRect(nameBtnX, y, nameBtnW, 30);
    ctx.strokeRect(nameBtnX, y, nameBtnW, 30);

    ctx.fillStyle = '#eee';
    ctx.font = '18px monospace';
    ctx.textAlign = 'left';
    ctx.fillText(m.names[i], nameBtnX + 10, y + 21);

    // Edit pencil
    ctx.fillStyle = '#888';
    ctx.font = '16px monospace';
    ctx.textAlign = 'right';
    ctx.fillText('\u270E', nameBtnX + nameBtnW - 8, y + 21);

    hitBoxes.push(box(nameBtnX, y, nameBtnW, 30, `edit-name:${i}`));

    // AI level cycle button
    const lv = m.aiLevels[i] ?? 'human';
    const isAI = lv !== 'human';
    ctx.fillStyle = isAI ? '#503' : '#222';
    ctx.fillRect(aiBtnX, y, aiBtnW, 30);
    ctx.strokeStyle = isAI ? '#a4b' : '#666';
    ctx.lineWidth = 1;
    ctx.strokeRect(aiBtnX, y, aiBtnW, 30);
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 14px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(aiLabel(lv), aiBtnX + aiBtnW / 2, y + 16);
    ctx.textBaseline = 'alphabetic';
    hitBoxes.push(box(aiBtnX, y, aiBtnW, 30, `cycle-ai:${i}`));
  }

  // "First to N wins" selector
  const targetY = rowY0 + m.playerCount * rowH + 14;
  ctx.fillStyle = '#ccc';
  ctx.font = 'bold 16px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('FIRST TO', GAME_W / 2 - 80, targetY + 18);
  drawPillButton(ctx, GAME_W / 2 - 10, targetY, 36, 30, '\u25C4', m.matchTarget > 1);
  hitBoxes.push(box(GAME_W / 2 - 10, targetY, 36, 30, 'target-dec'));
  ctx.fillStyle = '#fff';
  ctx.font = 'bold 22px monospace';
  ctx.fillText(String(m.matchTarget), GAME_W / 2 + 40, targetY + 22);
  drawPillButton(ctx, GAME_W / 2 + 60, targetY, 36, 30, '\u25BA', m.matchTarget < 5);
  hitBoxes.push(box(GAME_W / 2 + 60, targetY, 36, 30, 'target-inc'));
  ctx.fillStyle = '#ccc';
  ctx.font = 'bold 16px monospace';
  ctx.fillText('WINS', GAME_W / 2 + 130, targetY + 18);

  // Starting-cash selector ("BIGGER CASH = BIGGER BOOMS")
  const cashY = targetY + 46;
  ctx.fillStyle = '#ccc';
  ctx.font = 'bold 16px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('STARTING CASH', GAME_W / 2 - 120, cashY + 18);
  const cashIdx = CASH_TIERS.indexOf(m.startingCash as typeof CASH_TIERS[number]);
  const hasPrev = cashIdx > 0;
  const hasNext = cashIdx >= 0 && cashIdx < CASH_TIERS.length - 1;
  drawPillButton(ctx, GAME_W / 2 - 30, cashY, 36, 30, '\u25C4', hasPrev);
  hitBoxes.push(box(GAME_W / 2 - 30, cashY, 36, 30, 'cash-dec'));
  ctx.fillStyle = '#ffd060';
  ctx.font = 'bold 20px monospace';
  ctx.fillText(formatCash(m.startingCash), GAME_W / 2 + 40, cashY + 22);
  drawPillButton(ctx, GAME_W / 2 + 94, cashY, 36, 30, '\u25BA', hasNext);
  hitBoxes.push(box(GAME_W / 2 + 94, cashY, 36, 30, 'cash-inc'));

  // HOT SEAT / HOST CODE / JOIN CODE buttons (side-by-side near bottom)
  const startW = 180, startH = 60, gapBtn = 14;
  const totalW = startW * 3 + gapBtn * 2;
  const startX = (GAME_W - totalW) / 2;
  const startY = GAME_H - 110;
  drawPillButton(ctx, startX, startY, startW, startH, 'HOT SEAT', true, '#c44', '#f66');
  hitBoxes.push(box(startX, startY, startW, startH, 'start'));
  drawPillButton(ctx, startX + startW + gapBtn, startY, startW, startH, 'HOST CODE', true, '#262', '#4a4');
  hitBoxes.push(box(startX + startW + gapBtn, startY, startW, startH, 'code-host'));
  drawPillButton(ctx, startX + (startW + gapBtn) * 2, startY, startW, startH, 'JOIN CODE', true, '#246', '#48a');
  hitBoxes.push(box(startX + (startW + gapBtn) * 2, startY, startW, startH, 'code-join'));

  // Controls hint
  ctx.fillStyle = '#666';
  ctx.font = '12px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('KEYS: \u2190\u2192 angle  \u2191\u2193 power  SHIFT=\u00D710  ALT=\u00F710  SPACE=fire', GAME_W / 2, GAME_H - 30);
}

// ---------------------- PAUSE MENU ----------------------

export function drawPauseMenu(ctx: CanvasRenderingContext2D, soundOn: boolean): void {
  hitBoxes = [];

  // Dim background
  ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
  ctx.fillRect(0, 0, GAME_W, GAME_H);

  ctx.fillStyle = '#fff';
  ctx.font = 'bold 48px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('PAUSED', GAME_W / 2, 150);

  const btnW = 280, btnH = 54;
  const btnX = (GAME_W - btnW) / 2;
  let y = 200;
  const gap = 14;

  drawPillButton(ctx, btnX, y, btnW, btnH, 'RESUME', true, '#2a6', '#4a8');
  hitBoxes.push(box(btnX, y, btnW, btnH, 'resume'));
  y += btnH + gap;

  drawPillButton(ctx, btnX, y, btnW, btnH, soundOn ? 'SOUND: ON' : 'SOUND: OFF', true, '#335', '#557');
  hitBoxes.push(box(btnX, y, btnW, btnH, 'toggle-sound'));
  y += btnH + gap;

  drawPillButton(ctx, btnX, y, btnW, btnH, 'NEW GAME', true, '#c44', '#f66');
  hitBoxes.push(box(btnX, y, btnW, btnH, 'new-game'));
  y += btnH + gap;

  drawPillButton(ctx, btnX, y, btnW, btnH, 'TITLE SCREEN', true, '#446', '#668');
  hitBoxes.push(box(btnX, y, btnW, btnH, 'title'));
}

// ---------------------- MATCH OVER ----------------------

export function drawMatchOver(ctx: CanvasRenderingContext2D, m: MenuState): void {
  hitBoxes = [];

  // Dark backdrop
  ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
  ctx.fillRect(0, 0, GAME_W, GAME_H);

  if (m.matchWinner < 0 || m.matchWinner >= m.playerCount) return;

  const color = PLAYER_COLORS[m.matchWinner % PLAYER_COLORS.length];

  // Trophy emoji
  ctx.save();
  ctx.shadowColor = color.hex;
  ctx.shadowBlur = 40;
  ctx.fillStyle = '#ffd040';
  ctx.font = 'bold 96px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('\u{1F3C6}', GAME_W / 2, 140);
  ctx.restore();

  ctx.fillStyle = '#fff';
  ctx.font = 'bold 32px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('MATCH WINNER', GAME_W / 2, 190);

  ctx.fillStyle = color.hex;
  ctx.font = 'bold 56px monospace';
  ctx.fillText(m.names[m.matchWinner], GAME_W / 2, 260);

  // Final score
  ctx.fillStyle = '#aaa';
  ctx.font = '18px monospace';
  const scoreParts: string[] = [];
  for (let i = 0; i < m.playerCount; i++) {
    scoreParts.push(`${m.names[i]}: ${m.wins[i]}`);
  }
  ctx.fillText(scoreParts.join('   \u2022   '), GAME_W / 2, 300);

  // Buttons
  const btnW = 260, btnH = 54;
  const btnX = (GAME_W - btnW) / 2;
  let y = 360;
  const gap = 16;

  drawPillButton(ctx, btnX, y, btnW, btnH, 'REMATCH', true, '#2a6', '#4a8');
  hitBoxes.push(box(btnX, y, btnW, btnH, 'rematch'));
  y += btnH + gap;

  drawPillButton(ctx, btnX, y, btnW, btnH, 'TITLE SCREEN', true, '#446', '#668');
  hitBoxes.push(box(btnX, y, btnW, btnH, 'title'));
}

// ---------------------- SHARE LINK ----------------------

export interface ShareLinkInfo {
  url: string;
  nextPlayerName: string;
  isMatchOver: boolean;
  matchWinnerName?: string;
}

export function drawShareLink(ctx: CanvasRenderingContext2D, info: ShareLinkInfo): void {
  hitBoxes = [];

  // Backdrop
  ctx.fillStyle = 'rgba(5, 5, 15, 0.95)';
  ctx.fillRect(0, 0, GAME_W, GAME_H);

  // Title
  ctx.fillStyle = '#fff';
  ctx.font = 'bold 36px monospace';
  ctx.textAlign = 'center';
  ctx.fillText(info.isMatchOver ? 'MATCH OVER' : 'TURN COMPLETE', GAME_W / 2, 90);

  if (info.isMatchOver && info.matchWinnerName) {
    ctx.fillStyle = '#ffd040';
    ctx.font = 'bold 22px monospace';
    ctx.fillText(`Winner: ${info.matchWinnerName}`, GAME_W / 2, 130);
  }

  // Instruction
  ctx.fillStyle = '#aaffaa';
  ctx.font = 'bold 22px monospace';
  ctx.fillText(
    info.isMatchOver ? 'Send the final link to record the result:' : `Send this link to ${info.nextPlayerName}:`,
    GAME_W / 2, 180,
  );

  // URL display box
  const boxW = GAME_W - 80, boxH = 80;
  const boxX = 40, boxY = 210;
  ctx.fillStyle = '#101020';
  ctx.fillRect(boxX, boxY, boxW, boxH);
  ctx.strokeStyle = '#4a4';
  ctx.lineWidth = 2;
  ctx.strokeRect(boxX, boxY, boxW, boxH);

  // Wrap URL across lines
  ctx.fillStyle = '#9f9';
  ctx.font = '14px monospace';
  ctx.textAlign = 'left';
  const charsPerLine = Math.floor((boxW - 20) / 8.5);
  const lines: string[] = [];
  for (let i = 0; i < info.url.length; i += charsPerLine) {
    lines.push(info.url.slice(i, i + charsPerLine));
  }
  for (let i = 0; i < Math.min(lines.length, 4); i++) {
    ctx.fillText(lines[i], boxX + 10, boxY + 24 + i * 18);
  }

  // Buttons
  const btnW = 220, btnH = 56;
  const gap = 16;
  const btnY = 330;

  // Copy / Share
  const hasShare = typeof (navigator as Navigator & { share?: unknown }).share === 'function';
  if (hasShare) {
    const totalW = btnW * 2 + gap;
    const left = (GAME_W - totalW) / 2;
    drawPillButton(ctx, left, btnY, btnW, btnH, 'COPY LINK', true, '#446', '#88c');
    hitBoxes.push(box(left, btnY, btnW, btnH, 'share-copy'));
    drawPillButton(ctx, left + btnW + gap, btnY, btnW, btnH, 'SHARE \u2197', true, '#262', '#4a4');
    hitBoxes.push(box(left + btnW + gap, btnY, btnW, btnH, 'share-native'));
  } else {
    const left = (GAME_W - btnW) / 2;
    drawPillButton(ctx, left, btnY, btnW, btnH, 'COPY LINK', true, '#262', '#4a4');
    hitBoxes.push(box(left, btnY, btnW, btnH, 'share-copy'));
  }

  // Continue (for same-device hot-seat play)
  const contW = 280, contH = 50;
  const contX = (GAME_W - contW) / 2;
  const contY = btnY + btnH + 20;
  drawPillButton(ctx, contX, contY, contW, contH, info.isMatchOver ? 'BACK TO TITLE' : 'CONTINUE ON THIS DEVICE', true, '#333', '#666');
  hitBoxes.push(box(contX, contY, contW, contH, info.isMatchOver ? 'title' : 'share-continue'));

  // Footer hint
  ctx.fillStyle = '#777';
  ctx.font = '12px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('Anyone with the link can play the next turn.', GAME_W / 2, GAME_H - 20);
}

// ---------------------- CODE WAIT ----------------------

export interface CodeWaitInfo {
  code: string;
  /** What's happening: 'hosting' (waiting for joiner), 'waiting' (joiner played, waiting for opponent's move), 'error' */
  status: 'hosting' | 'waiting' | 'error';
  message?: string;
  /** Name of player we're waiting on (when known). */
  waitingFor?: string;
}

export function drawCodeWait(ctx: CanvasRenderingContext2D, info: CodeWaitInfo): void {
  hitBoxes = [];

  // Backdrop
  const bg = ctx.createLinearGradient(0, 0, 0, GAME_H);
  bg.addColorStop(0, '#0a0618');
  bg.addColorStop(1, '#1a0830');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, GAME_W, GAME_H);

  // Title
  ctx.fillStyle = '#fff';
  ctx.font = 'bold 32px monospace';
  ctx.textAlign = 'center';
  ctx.fillText(info.status === 'hosting' ? 'WAITING FOR OPPONENT' : info.status === 'error' ? 'CONNECTION ISSUE' : 'WAITING FOR THEIR TURN', GAME_W / 2, 90);

  // Code display (huge, centered)
  ctx.fillStyle = '#aaffaa';
  ctx.font = 'bold 22px monospace';
  ctx.fillText('SHARE THIS CODE', GAME_W / 2, 150);

  const codeBoxW = 360, codeBoxH = 110;
  const codeBoxX = (GAME_W - codeBoxW) / 2;
  const codeBoxY = 175;
  ctx.fillStyle = '#101020';
  ctx.fillRect(codeBoxX, codeBoxY, codeBoxW, codeBoxH);
  ctx.strokeStyle = '#4a4';
  ctx.lineWidth = 3;
  ctx.strokeRect(codeBoxX, codeBoxY, codeBoxW, codeBoxH);

  ctx.save();
  ctx.shadowColor = '#4f4';
  ctx.shadowBlur = 24;
  ctx.fillStyle = '#9f9';
  ctx.font = 'bold 72px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(info.code || '-----', GAME_W / 2, codeBoxY + codeBoxH / 2);
  ctx.textBaseline = 'alphabetic';
  ctx.restore();

  // Status message
  ctx.fillStyle = info.status === 'error' ? '#f88' : '#ccc';
  ctx.font = '16px monospace';
  ctx.textAlign = 'center';
  const msg = info.message
    ?? (info.status === 'hosting'
      ? 'They open scorch-mobile.vercel.app and tap JOIN CODE.'
      : info.status === 'waiting'
        ? `Waiting for ${info.waitingFor ?? 'opponent'} to fire...`
        : '');
  ctx.fillText(msg, GAME_W / 2, codeBoxY + codeBoxH + 30);

  // Spinner dots (animated by frame count)
  if (info.status !== 'error') {
    const t = Math.floor(performance.now() / 350) % 3;
    ctx.fillStyle = '#888';
    ctx.font = 'bold 22px monospace';
    ctx.fillText('. '.repeat(t + 1).trim(), GAME_W / 2, codeBoxY + codeBoxH + 60);
  }

  // Buttons
  const btnW = 220, btnH = 50, gap = 16;
  const btnY = GAME_H - 130;
  const totalW = btnW * 2 + gap;
  const left = (GAME_W - totalW) / 2;
  drawPillButton(ctx, left, btnY, btnW, btnH, 'COPY CODE', true, '#446', '#88c');
  hitBoxes.push(box(left, btnY, btnW, btnH, 'code-copy'));
  drawPillButton(ctx, left + btnW + gap, btnY, btnW, btnH, 'CANCEL', true, '#622', '#a44');
  hitBoxes.push(box(left + btnW + gap, btnY, btnW, btnH, 'title'));

  // Footer
  ctx.fillStyle = '#777';
  ctx.font = '12px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('Match expires after 24h of inactivity.', GAME_W / 2, GAME_H - 30);
}

// ---------------------- LOBBY ----------------------

export interface LobbySlot {
  name: string;
  /** 'local' = this device, 'remote' = another device, 'empty' = waiting */
  kind: 'local' | 'remote' | 'empty';
  /** Show a × kick button on this row? (Host sees × on remote slots only.) */
  canKick?: boolean;
}

export interface LobbyInfo {
  code: string;                 // '-----' while creating
  slots: LobbySlot[];           // length = maxSeats
  isHost: boolean;              // true = show START GAME button
  statusMessage?: string;       // optional footer line
  errorMessage?: string;        // red error line (e.g. game-already-started)
}

export function drawLobby(ctx: CanvasRenderingContext2D, info: LobbyInfo): void {
  hitBoxes = [];

  // Backdrop
  const bg = ctx.createLinearGradient(0, 0, 0, GAME_H);
  bg.addColorStop(0, '#0a0618');
  bg.addColorStop(1, '#1a0830');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, GAME_W, GAME_H);

  // Title
  ctx.fillStyle = '#fff';
  ctx.font = 'bold 32px monospace';
  ctx.textAlign = 'center';
  ctx.fillText(info.isHost ? 'HOSTING LOBBY' : 'JOINED LOBBY', GAME_W / 2, 70);

  // Code header
  ctx.fillStyle = '#aaffaa';
  ctx.font = 'bold 18px monospace';
  ctx.fillText('SHARE THIS CODE', GAME_W / 2, 110);

  // Code box
  const codeBoxW = 320, codeBoxH = 80;
  const codeBoxX = (GAME_W - codeBoxW) / 2;
  const codeBoxY = 125;
  ctx.fillStyle = '#101020';
  ctx.fillRect(codeBoxX, codeBoxY, codeBoxW, codeBoxH);
  ctx.strokeStyle = '#4a4';
  ctx.lineWidth = 3;
  ctx.strokeRect(codeBoxX, codeBoxY, codeBoxW, codeBoxH);

  ctx.save();
  ctx.shadowColor = '#4f4';
  ctx.shadowBlur = 20;
  ctx.fillStyle = '#9f9';
  ctx.font = 'bold 56px monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(info.code, GAME_W / 2, codeBoxY + codeBoxH / 2);
  ctx.textBaseline = 'alphabetic';
  ctx.restore();

  // Player slots
  const listTop = codeBoxY + codeBoxH + 30;
  ctx.fillStyle = '#ccc';
  ctx.font = 'bold 18px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('PLAYERS', GAME_W / 2, listTop);

  const rowW = 540, rowH = 44, rowGap = 8;
  const rowX = (GAME_W - rowW) / 2;
  const rowY0 = listTop + 20;
  for (let i = 0; i < info.slots.length; i++) {
    const slot = info.slots[i];
    const y = rowY0 + i * (rowH + rowGap);
    const color = PLAYER_COLORS[i % PLAYER_COLORS.length];

    // Row background
    ctx.fillStyle = slot.kind === 'empty' ? '#181825' : '#222236';
    ctx.fillRect(rowX, y, rowW, rowH);
    ctx.strokeStyle = slot.kind === 'empty' ? '#333' : '#555';
    ctx.lineWidth = 1;
    ctx.strokeRect(rowX, y, rowW, rowH);

    // Color chip
    ctx.fillStyle = slot.kind === 'empty' ? '#333' : color.hex;
    ctx.fillRect(rowX + 8, y + 8, 28, 28);

    // Name
    ctx.fillStyle = slot.kind === 'empty' ? '#666' : '#eee';
    ctx.font = 'bold 18px monospace';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    const nameText = slot.kind === 'empty' ? 'Waiting...' : slot.name;
    ctx.fillText(nameText, rowX + 48, y + rowH / 2);

    // Kick button (host-only, remote rows only). Push-right; label sits to its left.
    const kickSize = 32;
    const kickX = rowX + rowW - 8 - kickSize;
    const kickY = y + (rowH - kickSize) / 2;
    if (slot.canKick) {
      // Red round-rect with × glyph
      ctx.fillStyle = '#622';
      ctx.fillRect(kickX, kickY, kickSize, kickSize);
      ctx.strokeStyle = '#d55';
      ctx.lineWidth = 2;
      ctx.strokeRect(kickX, kickY, kickSize, kickSize);
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 22px monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('\u00D7', kickX + kickSize / 2, kickY + kickSize / 2 + 1);
      hitBoxes.push(box(kickX, kickY, kickSize, kickSize, `lobby-kick:${i}`));
    }

    // Right-hand label (positioned to the left of the kick button when present)
    let label = '';
    let labelColor = '#888';
    if (slot.kind === 'local') { label = 'LOCAL';  labelColor = '#8f8'; }
    else if (slot.kind === 'remote') { label = 'REMOTE'; labelColor = '#8bf'; }
    else { label = '—'; labelColor = '#555'; }
    ctx.fillStyle = labelColor;
    ctx.font = 'bold 14px monospace';
    ctx.textAlign = 'right';
    const labelRight = slot.canKick ? kickX - 10 : rowX + rowW - 12;
    ctx.fillText(label, labelRight, y + rowH / 2);
    ctx.textBaseline = 'alphabetic';
  }

  // Status / error line
  const msgY = rowY0 + info.slots.length * (rowH + rowGap) + 30;
  if (info.errorMessage) {
    ctx.fillStyle = '#f88';
    ctx.font = 'bold 16px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(info.errorMessage, GAME_W / 2, msgY);
  } else if (info.statusMessage) {
    ctx.fillStyle = '#ccc';
    ctx.font = '15px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(info.statusMessage, GAME_W / 2, msgY);
  }

  // Buttons
  const btnW = 220, btnH = 56, gap = 16;
  const btnY = GAME_H - 110;
  if (info.isHost) {
    const totalW = btnW * 3 + gap * 2;
    const left = (GAME_W - totalW) / 2;
    // START GAME (host only) — greyed out if no remotes yet
    const hasRemote = info.slots.some(s => s.kind === 'remote');
    drawPillButton(ctx, left, btnY, btnW, btnH, 'START GAME', hasRemote, '#262', '#4a4');
    if (hasRemote) hitBoxes.push(box(left, btnY, btnW, btnH, 'lobby-start'));
    drawPillButton(ctx, left + btnW + gap, btnY, btnW, btnH, 'COPY CODE', true, '#446', '#88c');
    hitBoxes.push(box(left + btnW + gap, btnY, btnW, btnH, 'code-copy'));
    drawPillButton(ctx, left + (btnW + gap) * 2, btnY, btnW, btnH, 'CANCEL', true, '#622', '#a44');
    hitBoxes.push(box(left + (btnW + gap) * 2, btnY, btnW, btnH, 'title'));
  } else {
    // Joiner: just COPY CODE + LEAVE
    const totalW = btnW * 2 + gap;
    const left = (GAME_W - totalW) / 2;
    drawPillButton(ctx, left, btnY, btnW, btnH, 'COPY CODE', true, '#446', '#88c');
    hitBoxes.push(box(left, btnY, btnW, btnH, 'code-copy'));
    drawPillButton(ctx, left + btnW + gap, btnY, btnW, btnH, 'LEAVE', true, '#622', '#a44');
    hitBoxes.push(box(left + btnW + gap, btnY, btnW, btnH, 'title'));
  }

  // Footer
  ctx.fillStyle = '#777';
  ctx.font = '12px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('Match expires after 24h of inactivity.', GAME_W / 2, GAME_H - 30);
}

// ---------------------- HELPERS ----------------------

function drawPillButton(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  label: string,
  enabled = true,
  bg = '#333',
  border = '#666',
): void {
  ctx.save();
  ctx.globalAlpha = enabled ? 1 : 0.4;
  ctx.fillStyle = bg;
  ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = border;
  ctx.lineWidth = 2;
  ctx.strokeRect(x, y, w, h);
  ctx.fillStyle = '#fff';
  ctx.font = `bold ${Math.min(22, h - 24)}px monospace`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, x + w / 2, y + h / 2);
  ctx.textBaseline = 'alphabetic';
  ctx.restore();
}

// ---------------------- SHOP ----------------------

export interface ShopView {
  shopperIndex: number;     // which tank is currently shopping
  shopperName: string;
  shopperColor: string;
  shopperCash: number;
  shopperInventory: Record<string, number>;
  catalog: WeaponDef[];     // filtered to buyable weapons
  scroll: number;           // top row index
  roundNumber: number;
  totalShoppers: number;    // e.g. "Shop 2 of 4"
  shopperOrder: number;     // 1-indexed
}

const SHOP_ROWS_VISIBLE = 10;
const SHOP_ROW_H = 38;

export function drawShop(ctx: CanvasRenderingContext2D, v: ShopView): void {
  hitBoxes = [];

  // Background gradient
  const bg = ctx.createLinearGradient(0, 0, 0, GAME_H);
  bg.addColorStop(0, '#0a1418');
  bg.addColorStop(1, '#182030');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, GAME_W, GAME_H);

  // Header: who's shopping + round
  ctx.fillStyle = v.shopperColor;
  ctx.font = 'bold 40px monospace';
  ctx.textAlign = 'center';
  ctx.fillText(v.shopperName.toUpperCase(), GAME_W / 2, 58);

  ctx.fillStyle = '#aaa';
  ctx.font = 'bold 18px monospace';
  ctx.fillText(
    `ARMORY  \u2022  ROUND ${v.roundNumber}  \u2022  SHOPPER ${v.shopperOrder} OF ${v.totalShoppers}`,
    GAME_W / 2,
    86,
  );

  // Cash panel (top-right)
  const cashX = GAME_W - 280;
  const cashY = 24;
  ctx.fillStyle = 'rgba(0,0,0,0.5)';
  ctx.fillRect(cashX, cashY, 256, 48);
  ctx.strokeStyle = '#ffd060';
  ctx.lineWidth = 2;
  ctx.strokeRect(cashX, cashY, 256, 48);
  ctx.fillStyle = '#ffd060';
  ctx.font = 'bold 22px monospace';
  ctx.textAlign = 'left';
  ctx.fillText('CASH', cashX + 14, cashY + 30);
  ctx.textAlign = 'right';
  ctx.fillText(formatCashFull(v.shopperCash), cashX + 244, cashY + 30);

  // Table layout
  const tableX = 60;
  const tableY = 120;
  const tableW = GAME_W - 120;

  // Column headers
  ctx.fillStyle = '#aaa';
  ctx.font = 'bold 14px monospace';
  ctx.textAlign = 'left';
  ctx.fillText('WEAPON', tableX + 12, tableY - 8);
  ctx.textAlign = 'right';
  ctx.fillText('PRICE', tableX + tableW - 320, tableY - 8);
  ctx.fillText('OWNED', tableX + tableW - 190, tableY - 8);
  ctx.textAlign = 'center';
  ctx.fillText('BUY / SELL', tableX + tableW - 80, tableY - 8);

  // Rows
  const total = v.catalog.length;
  const maxScroll = Math.max(0, total - SHOP_ROWS_VISIBLE);
  const scroll = Math.max(0, Math.min(maxScroll, v.scroll));
  const endRow = Math.min(total, scroll + SHOP_ROWS_VISIBLE);

  for (let row = scroll; row < endRow; row++) {
    const def = v.catalog[row];
    const y = tableY + (row - scroll) * SHOP_ROW_H;
    const rowBg = (row % 2) === 0 ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.15)';
    ctx.fillStyle = rowBg;
    ctx.fillRect(tableX, y, tableW, SHOP_ROW_H);

    // Name + category
    ctx.fillStyle = '#eee';
    ctx.font = 'bold 16px monospace';
    ctx.textAlign = 'left';
    ctx.fillText(def.name, tableX + 12, y + 24);
    ctx.fillStyle = '#667';
    ctx.font = '12px monospace';
    ctx.fillText(def.category.toUpperCase(), tableX + 200, y + 24);

    // Price
    const affordable = v.shopperCash >= def.cost;
    ctx.fillStyle = affordable ? '#ffd060' : '#664';
    ctx.font = 'bold 16px monospace';
    ctx.textAlign = 'right';
    ctx.fillText(formatCashFull(def.cost), tableX + tableW - 320, y + 24);

    // Owned
    const owned = v.shopperInventory[def.id] ?? 0;
    ctx.fillStyle = owned > 0 ? '#8f8' : '#555';
    ctx.textAlign = 'center';
    ctx.fillText(
      owned === Infinity ? '\u221E' : String(owned),
      tableX + tableW - 190,
      y + 24,
    );

    // Buy/sell buttons
    const btnY = y + 4;
    const btnH = 30;
    const minusX = tableX + tableW - 130;
    const plusX  = tableX + tableW - 40;
    drawPillButton(ctx, minusX, btnY, 40, btnH, '-', owned > 0 && owned !== Infinity, '#622', '#a44');
    hitBoxes.push(box(minusX, btnY, 40, btnH, `shop-sell:${def.id}`));
    drawPillButton(ctx, plusX, btnY, 40, btnH, '+', affordable, '#262', '#4a4');
    hitBoxes.push(box(plusX, btnY, 40, btnH, `shop-buy:${def.id}`));
  }

  // Scroll indicator / buttons
  if (scroll > 0) {
    drawPillButton(ctx, tableX + tableW - 80, tableY - 34, 30, 24, '\u25B2', true);
    hitBoxes.push(box(tableX + tableW - 80, tableY - 34, 30, 24, 'shop-scroll-up'));
  }
  const bottomY = tableY + SHOP_ROWS_VISIBLE * SHOP_ROW_H + 6;
  if (scroll < maxScroll) {
    drawPillButton(ctx, tableX + tableW - 80, bottomY, 30, 24, '\u25BC', true);
    hitBoxes.push(box(tableX + tableW - 80, bottomY, 30, 24, 'shop-scroll-down'));
  }

  // Done / Skip-all buttons
  const doneW = 220, doneH = 52;
  const doneY = GAME_H - 80;
  const doneX = GAME_W / 2 - doneW - 14;
  const skipX = GAME_W / 2 + 14;
  drawPillButton(ctx, doneX, doneY, doneW, doneH, 'DONE SHOPPING', true, '#2a6', '#4a8');
  hitBoxes.push(box(doneX, doneY, doneW, doneH, 'shop-done'));
  drawPillButton(ctx, skipX, doneY, doneW, doneH, 'SKIP ALL', true, '#446', '#668');
  hitBoxes.push(box(skipX, doneY, doneW, doneH, 'shop-skip-all'));

  // Hint
  ctx.fillStyle = '#667';
  ctx.font = '12px monospace';
  ctx.textAlign = 'center';
  ctx.fillText(
    'KEYS: \u2191\u2193 scroll  \u2190\u2192 prev/next weapon  + / - buy/sell  ENTER = DONE',
    GAME_W / 2,
    GAME_H - 16,
  );
}

function formatCashFull(n: number): string {
  return '$' + n.toLocaleString('en-US');
}

